import { NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserLevel } from '@reenstudio/app/core/enum/user-level';
import { UserRole } from '@reenstudio/app/core/enum/user-role';
import { CerAddUserApiRequest } from '@reenstudio/app/core/models/api/cer/cer-api-model';
import { SiteAddUserApiRequest } from '@reenstudio/app/core/models/api/site/site-api-model';
import { UserAddEditApiRequest } from '@reenstudio/app/core/models/api/user/user-api-model';
import { Cer } from '@reenstudio/app/core/models/cer/cer';
import { CerService } from '@reenstudio/app/core/services/api/cer.service';
import { SiteApiService } from '@reenstudio/app/core/services/api/site.services';
import { UserApiService } from '@reenstudio/app/core/services/api/user-service';
import { UserService } from '@reenstudio/app/core/services/user-service';
import { MaterialModule } from '@reenstudio/app/material.module';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { concatMap, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-user-add-edit',
  templateUrl: './user-add-edit.component.html',
  styleUrls: ['./user-add-edit.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgFor,
    NgIf,
    MaterialModule,
    FormsModule,
    NgxSkeletonLoaderModule,
  ],
})
export class UserAddEditComponent {
  @Input() type: string;

  loading: boolean = false;

  cerId: number;
  siteId: number;
  userId: string;
  selectRef: number;

  form: FormGroup;
  typeForm: FormGroup;

  cerList: Cer[];
  siteList: any[] = [];

  userType: string = 'userType';

  constructor(
    private userApiService: UserApiService,
    private userService: UserService,
    private cerService: CerService,
    private siteService: SiteApiService,
    private snackBarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: NGXLogger
  ) {}

  ngOnInit(): void {
    this.forminit();
  }

  get f() {
    return this.form.controls;
  }

  forminit() {
    this.loading = true;
    this.form = new FormGroup({
      name: new FormControl(null, Validators.required),
      email: new FormControl(null, [Validators.required, Validators.email]),
      cer: new FormControl(),
      site: new FormControl(),
      level: new FormControl(),
    });

    const params = { enabled: true };

    this.cerService
      .getCers(params)
      .pipe(
        concatMap((cersResponse) => {
          this.logger.debug(
            'Cer for User Add Edit recevived data',
            cersResponse
          );
          this.cerList = cersResponse.data;
          return this.siteService.getSites();
        }),
        concatMap((sitesResponse) => {
          this.logger.debug(
            'Sites for User Add Edit recevived data',
            sitesResponse
          );
          this.siteList = sitesResponse.data;

          if (this.type === 'edit') {
            return this.route.params.pipe(
              concatMap((params) => {
                this.userId = params['id'];
                if (this.userId) {
                  return this.userApiService.getUser(this.userId);
                } else {
                  return of(null);
                }
              })
            );
          } else {
            return of(null);
          }
        })
      )
      .subscribe({
        next: (userData) => {
          if (userData) {
            this.logger.debug('User edit received data', userData);
            this.getRoleName(userData.level_id);

            if (this.userType === UserRole.SITE) {
              this.siteId = userData.sites[0];
            } else if (this.userType === UserRole.CER) {
              this.cerId = userData.cers[0];
            }

            this.form.patchValue({
              name: userData.name,
              email: userData.email,
              level: userData.level_id,
              site: this.userType === UserRole.SITE ? userData.sites[0] : null,
              cer: this.userType === UserRole.CER ? userData.cers[0] : null,
            });

            this.userId = userData.id;
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Errore durante le chiamate:', err);
        },
      });
  }

  onSubmit() {
    this.loading = true;
    this.logger.debug('Form sent data', this.form.value);

    let userData: UserAddEditApiRequest = {
      name: this.form.value.name,
      email: this.form.value.email,
      level: this.form.value.level,
    };

    const userType = this.form.value.level;
    if (userType === 'cer') {
      userData.level = UserLevel.CER;
      this.form.value.level = UserLevel.CER;
    } else if (userType === 'site') {
      userData.level = UserLevel.SITE;
      this.form.value.level = UserLevel.SITE;
    }

    if (this.type === 'add') {
      this.userApiService
        .addUser(userData)
        .pipe(
          switchMap((userResponse) => {
            this.logger.debug('User Response', userResponse);
            const userId = userResponse.details.user.id;
            if (userData.level === UserLevel.CER) {
              const params: CerAddUserApiRequest = {
                cer: this.form.value.cer,
              };
              return this.cerService.addCerUser(userId, params);
            } else if (userData.level === UserLevel.SITE) {
              const params: SiteAddUserApiRequest = {
                site: this.form.value.site,
              };
              return this.siteService.addSiteUser(userId, params);
            }
            return of(null);
          })
        )
        .subscribe({
          next: () => {
            this.snackBarService.success('Utente aggiunto con successo');
            this.loading = false;
            this.router.navigate(['/administration/users']);
          },
          error: (err) => {
            console.error('Add User errore durante le chiamate:', err);
          },
        });
    } else if (this.type === 'edit') {
      this.userApiService
        .editUser(this.userId, userData)
        .pipe(
          switchMap((userEditResponse) => {
            this.logger.debug('User Edit Response', userEditResponse);

            //MODIFICA DEL LIVELLO QUANDO SARA' POSSIBILE
            // this.logger.debug('User Edit level', userData.level);
            // this.logger.debug('User Edit siteId', this.siteId);
            // this.logger.debug('User Edit cerId', this.cerId);
            // this.logger.debug('User Edit userId', this.userId);

            // let eidtedType = null;
            // if (
            //   this.form.value.level === UserLevel.CER &&
            //   this.userType === 'Sito'
            // ) {
            //   if (this.siteId) {
            //     this.logger.debug(
            //       '[Level] Unlink user from site ',
            //       this.siteId
            //     );
            //     eidtedType = 'deleteSiteUser';
            //     const params = {
            //       site: this.siteId,
            //     };
            //     return this.siteService.deleteSiteUser(this.userId, params);
            //   }
            // } else if (
            //   this.form.value.level === UserLevel.SITE &&
            //   this.userType === 'Cer'
            // ) {
            //   if (this.cerId) {
            //     this.logger.debug('[Level] Unlink user from cer ', this.cerId);
            //     eidtedType = 'deleteCerUser';
            //     const params = {
            //       cer: this.cerId,
            //     };
            //     return this.cerService.deleteCerUser(this.userId, params);
            //   }
            // } else if (
            //   this.form.value.level === userData.level &&
            //   this.form.value.site !== this.siteId
            // ) {
            //   if (this.siteId) {
            //     this.logger.debug('Unlink user from site ', this.siteId);

            //     eidtedType = 'deleteSiteUser';
            //     const params = {
            //       site: this.siteId,
            //     };
            //     return this.siteService.deleteSiteUser(this.userId, params);
            //   }
            // } else if (
            //   this.form.value.level === userData.level &&
            //   this.form.value.cer !== this.cerId
            // ) {
            //   if (this.cerId) {
            //     this.logger.debug('Unlink user from cer ', this.cerId);
            //     eidtedType = 'deleteCerUser';
            //     const params = {
            //       cer: this.cerId,
            //     };
            //     return this.cerService.deleteCerUser(this.userId, params);
            //   }
            // }
            // return of(eidtedType);
            return of(null);
          })
          // CONTINUA MODIFICA DEL LIVELLO QUANDO SARA' POSSIBILE
          // switchMap((editedType) => {
          //   this.logger.debug('User editedType Response', editedType);
          //   if (
          //     editedType ||
          //     (this.form.value.level === userData.level &&
          //       this.form.value.cer !== this.cerId) ||
          //     (this.form.value.level === userData.level &&
          //       this.form.value.site !== this.siteId) ||
          //     (this.form.value.level === UserLevel.SITE &&
          //       this.userType === 'Cer') ||
          //     (this.form.value.level === UserLevel.CER &&
          //       this.userType === 'Sito')
          //   ) {
          //     if (userData.level === UserLevel.CER) {
          //       const params: CerAddUserApiRequest = {
          //         cer: this.form.value.cer,
          //       };
          //       this.logger.debug('Link user to cer ', this.form.value.cer);
          //       return this.cerService.addCerUser(this.userId, params);
          //     } else if (userData.level === UserLevel.SITE) {
          //       const params: SiteAddUserApiRequest = {
          //         site: this.form.value.site,
          //       };
          //       this.logger.debug('Link user to site ', this.form.value.site);
          //       return this.siteService.addSiteUser(this.userId, params);
          //     }
          //   }
          //   return of(null);
          // })
        )
        .subscribe({
          next: () => {
            this.snackBarService.success('Utente modificato con successo');
            this.loading = false;
            this.router.navigate(['/administration/users']);
          },
          error: (err) => {
            console.error('Edit user errore durante le chiamate:', err);
          },
        });
    }
  }

  getRoleName(id: number) {
    const userRole = this.userService.getRoleById(id);
    if (userRole) {
      this.userType = userRole;
    }
  }
}
